import React, { useState } from "react";
import loadable from "@loadable/component";
import { ProductCardSerializer } from "web/types/serializers";
import analytics from "web/script/analytics/analytics";
import browser from "web/script/utils/browser";
import { BREAKPOINTS } from "web/react/constants";
import Carousel from "web/react/components/product-gallery/product-gallery-carousel/product-gallery-carousel";

const FullscreenGallery = loadable(() =>
    import(
        /* webpackChunkName: "react-fullscreen-gallery" */ "web/react/components/product-gallery/product-gallery-fullscreen/product-gallery-fullscreen"
    )
);

interface ProductGalleryProps {
    images: ProductImage[];
    relatedProducts?: ProductCardSerializer[];
    currentLinkId?: string;
    swipeRedirect?: boolean;
    hasRelatedProductsSidebar: boolean;
    analyticsAction?: string;
    variant?: "oos-carousel";
}

function ProductGallery({
    images,
    relatedProducts,
    currentLinkId,
    swipeRedirect,
    hasRelatedProductsSidebar,
    analyticsAction,
    variant,
}: ProductGalleryProps): React.ReactElement {
    const viewport = browser.getViewport() === browser.VIEWPORTS.DESKTOP ? "desktop" : "mobile";
    const analyticsActionString = analyticsAction || `${viewport}_gallery`;
    const [firstInteraction, setFirstInteraction] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const oosCarouselVariant = variant === "oos-carousel";

    function handleFirstInteraction(): void {
        setFirstInteraction(true);
        analytics.event("engagement", analyticsActionString, "interacted");
    }

    function openFullscreenGallery(index): void {
        setCurrentSlideIndex(index);
        setFullscreen(true);
    }

    function handleImageClick(index): void {
        if (!firstInteraction) {
            handleFirstInteraction();
        }

        openFullscreenGallery(index);
    }

    // Slick settings and props for the gallery
    const baseGallerySettings = {
        speed: 0,
        initialSlide: 0,
        arrows: false,
        dots: images.length > 1,
        // The progressive option loads the first image on page load and the other images are loaded afterwards
        lazyLoad: "progressive",
        centerMode: false,
        slidesToShow: 1,
        infinite: false,
        variableWidth: false,
        swipe: false,
        touchMove: false,
        draggable: false,
        responsive: [
            {
                breakpoint: BREAKPOINTS.Medium,
                settings: {
                    speed: 200,
                    slidesToShow: images.length > 1 ? 1.3 : 1,
                    infinite: true,
                    variableWidth: true,
                    swipe: true,
                    touchMove: true,
                    touchThreshold: 20,
                    draggable: true,
                },
            },
        ],
    };

    const oosCarouselSettings = {
        speed: 200,
        arrows: true,
        slidesToShow: images.length > 1 ? 2 : 1,
        infinite: true,
        responsive: [
            {
                breakpoints: BREAKPOINTS.Medium,
                settings: {
                    arrows: false,
                    slidesToShow: images.length > 1 ? 1 : 1,
                },
            },
        ],
    };

    const gallerySettings = {
        ...baseGallerySettings,
        // overwrite base settings only if screen size applies, otherwise the mobile oosCarouselVariant will be buggy (library limitation)
        ...(oosCarouselVariant && window.innerWidth > BREAKPOINTS.Medium
            ? oosCarouselSettings
            : {}),
        responsive: [
            {
                ...baseGallerySettings.responsive[0],
                settings: {
                    ...baseGallerySettings.responsive[0].settings,
                    ...(oosCarouselVariant ? oosCarouselSettings.responsive[0].settings : {}),
                },
            },
        ],
    };

    const galleryProps = {
        analyticsAction: analyticsActionString,
        firstInteraction: firstInteraction,
        handleFirstInteraction: handleFirstInteraction,
        images: images,
        relatedProducts: relatedProducts,
        handleImageClick: (index): void => handleImageClick(index),
        openFullscreenGallery: (index): void => openFullscreenGallery(index),
        settings: gallerySettings,
        currentLinkId: currentLinkId,
        swipeRedirect: swipeRedirect,
        hasRelatedProductsSidebar: hasRelatedProductsSidebar,
        variant: variant,
    };

    // Slick settings and props for the fullscreen gallery
    const fullscreenGallerySettings = {
        speed: 400,
        initialSlide: currentSlideIndex,
        slidesToShow: 1,
        dots: images.length > 1,
        // The progressive option loads the first image on page load and the other images are loaded afterwards
        lazyLoad: false,
        centerMode: false,
        touchThreshold: 20,
        infinite: true,
        swipeToSlide: false,
        arrows: true,
        swipe: false,
        touchMove: false,
        draggable: false,
        responsive: [
            {
                breakpoint: BREAKPOINTS.Medium,
                settings: {
                    speed: 200,
                    arrows: false,
                    swipeToSlide: true,
                    swipe: true,
                    touchMove: true,
                    draggable: true,
                    lazyLoad: "progressive",
                },
            },
        ],
    };

    const fullscreenGalleryProps = {
        analyticsAction: analyticsActionString,
        isOpen: fullscreen,
        onClose: (): void => setFullscreen(false),
        images: images,
        relatedProducts: relatedProducts,
        firstInteraction: firstInteraction,
        currentLinkId: currentLinkId,
        settings: fullscreenGallerySettings,
    };

    return (
        <>
            <Carousel {...galleryProps} />
            {fullscreen ? <FullscreenGallery {...fullscreenGalleryProps} /> : null}
        </>
    );
}

export default ProductGallery;

ProductGallery.displayName = "ProductGallery";
